"use client";

import { c as _c } from "react/compiler-runtime";
import { isAddressEqual, normalizeAddress } from "@opensea/utils";
import { useConnect, useConnections, usePreviouslyConnectedAccounts, WalletRejectedError } from "@opensea/wallet";
import { captureException } from "@sentry/nextjs";
import { keyBy } from "es-toolkit";
import { useCallback, useMemo } from "react";
import { useAddress } from "./use-address";
export function useWalletConnectionResolver(t0) {
  const $ = _c(21);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    connections: overrideConnections
  } = t1;
  const defaultConnections = useConnections();
  const connections = overrideConnections ?? defaultConnections;
  const {
    connect,
    getConnector,
    activeConnector
  } = useConnect();
  const previouslyConnectedAccounts = usePreviouslyConnectedAccounts();
  const activeAddress = useAddress();
  let t2;
  let t3;
  if ($[2] !== previouslyConnectedAccounts) {
    t3 = keyBy(previouslyConnectedAccounts, _temp);
    $[2] = previouslyConnectedAccounts;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  t2 = t3;
  const previouslyConnectedAccountsByAddress = t2;
  let t4;
  let t5;
  if ($[4] !== connections) {
    t5 = new Map(connections.flatMap(_temp2));
    $[4] = connections;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  t4 = t5;
  const connectionsByAddress = t4;
  let t6;
  if ($[6] !== connectionsByAddress || $[7] !== previouslyConnectedAccountsByAddress) {
    t6 = (address, options) => {
      if (!address) {
        return;
      }
      const normalizedAddress = normalizeAddress(address);
      const existingConnection = connectionsByAddress.get(normalizedAddress);
      if (existingConnection) {
        return existingConnection.connector.id;
      }
      if (options?.lastSeenConnectorId) {
        return options.lastSeenConnectorId || undefined;
      }
      return previouslyConnectedAccountsByAddress[normalizedAddress]?.connectorId;
    };
    $[6] = connectionsByAddress;
    $[7] = previouslyConnectedAccountsByAddress;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const resolveConnectorId = t6;
  let t7;
  if ($[9] !== getConnector || $[10] !== resolveConnectorId) {
    t7 = (address_0, options_0) => {
      const connectorId = resolveConnectorId(address_0, options_0);
      return connectorId ? getConnector(connectorId) : undefined;
    };
    $[9] = getConnector;
    $[10] = resolveConnectorId;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const resolveConnector = t7;
  let t8;
  if ($[12] !== activeAddress || $[13] !== activeConnector || $[14] !== connect || $[15] !== connectionsByAddress || $[16] !== getConnector || $[17] !== previouslyConnectedAccountsByAddress || $[18] !== resolveConnector || $[19] !== resolveConnectorId) {
    t8 = {
      connect,
      getConnector,
      activeConnector,
      activeAddress,
      connectionsByAddress,
      previouslyConnectedAccountsByAddress,
      resolveConnectorId,
      resolveConnector
    };
    $[12] = activeAddress;
    $[13] = activeConnector;
    $[14] = connect;
    $[15] = connectionsByAddress;
    $[16] = getConnector;
    $[17] = previouslyConnectedAccountsByAddress;
    $[18] = resolveConnector;
    $[19] = resolveConnectorId;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
function _temp2(connection) {
  return connection.addresses.map(connAddress => [normalizeAddress(connAddress), connection]);
}
function _temp(account) {
  return normalizeAddress(account.address);
}
export function useEnsureWalletConnection() {
  const $ = _c(9);
  const {
    connect,
    getConnector,
    activeConnector,
    activeAddress,
    connectionsByAddress,
    resolveConnectorId
  } = useWalletConnectionResolver();
  let t0;
  if ($[0] !== activeAddress || $[1] !== activeConnector || $[2] !== connect || $[3] !== connectionsByAddress || $[4] !== getConnector || $[5] !== resolveConnectorId) {
    t0 = async t1 => {
      const {
        address,
        onConnected,
        onMissingLinkedWallet,
        onSwitchFailed,
        lastSeenConnectorId
      } = t1;
      if (!address) {
        await onConnected();
        return;
      }
      const normalizedAddress = normalizeAddress(address);
      const isAlreadyActive = Boolean(activeConnector) && isAddressEqual(activeAddress || undefined, address) && connectionsByAddress.get(normalizedAddress)?.connector.id === activeConnector?.id;
      if (isAlreadyActive) {
        await onConnected();
        return;
      }
      const notifySwitchFailed = connector => {
        if (connector) {
          onSwitchFailed?.({
            walletName: connector.name
          });
        } else {
          onSwitchFailed?.();
        }
      };
      let attemptedConnection = false;
      const tryConnect = async connector_0 => {
        if (!connector_0) {
          return false;
        }
        attemptedConnection = true;
        ;
        try {
          await connect(connector_0, {
            address
          });
          return true;
        } catch (t2) {
          const error = t2;
          if (!(error instanceof WalletRejectedError)) {
            captureException(error, {
              extra: {
                address,
                connectorId: connector_0.id
              },
              tags: {
                source: "useEnsureWalletConnection"
              }
            });
            notifySwitchFailed(connector_0);
          }
          return false;
        }
      };
      const existingConnection = connectionsByAddress.get(normalizedAddress);
      const targetConnectorId = existingConnection?.connector.id ?? resolveConnectorId(address, {
        lastSeenConnectorId
      });
      const targetConnector = targetConnectorId ? getConnector(targetConnectorId) : undefined;
      if (activeConnector && !isAlreadyActive) {
        if (targetConnector) {
          notifySwitchFailed(targetConnector);
        } else {
          onMissingLinkedWallet?.();
        }
        return;
      }
      if (targetConnector) {
        const didConnect = await tryConnect(targetConnector);
        if (didConnect) {
          await onConnected();
          return;
        }
      }
      if (!attemptedConnection) {
        onMissingLinkedWallet?.();
      }
    };
    $[0] = activeAddress;
    $[1] = activeConnector;
    $[2] = connect;
    $[3] = connectionsByAddress;
    $[4] = getConnector;
    $[5] = resolveConnectorId;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  const ensureWalletConnection = t0;
  let t1;
  if ($[7] !== ensureWalletConnection) {
    t1 = {
      ensureWalletConnection
    };
    $[7] = ensureWalletConnection;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}